const LOCALES = require('../common/locales/locales.json');

const { contentTypeMap } = require('../config/content-type-map');
const { slugify } = require('./string-helper-functions');
const { categoryCodes } = require('./global-commitment/category-codes');
const {
  GLOBAL_COMMITMENT_BASE_PATH,
  GLOBAL_COMMITMENT_LISTING_PAGE_SLUG,
} = require('../variables');

const pageTypeTranslations = {};
LOCALES.forEach(locale => {
  pageTypeTranslations[locale.code] = require(
    `../common/locales/${locale.code}/page-type-translations.json`
  );
});

function generateUrl(localeCode, pageType, slug) {
  const baseUrl = getBaseUrl(localeCode);
  const translatedPageTypeSlug = getTranslatedPageTypeSlug(
    localeCode,
    pageType
  );
  const url = baseUrl + translatedPageTypeSlug + slug;
  return url;
}

function getBaseUrl(localeCode = 'en-GB') {
  if (localeCode === 'en-GB') {
    return '/';
  }
  return `/${localeCode.slice(0, 2)}/`;
}

function getTranslatedPageTypeSlug(localeCode, pageType) {
  const standalonePageTypes = ['basic', 'standard'];
  if (standalonePageTypes.includes(pageType)) {
    return '';
  } else {
    const translatedPageTypeSlug = pageTypeTranslations[localeCode][pageType]
      ? pageTypeTranslations[localeCode][pageType].pageTypeUrl
      : pageType;
    return `${translatedPageTypeSlug}/`;
  }
}

// generates a url for a given entry of type: standard page, sub page or circular example page.
// @TODO refactor so enabled states are passed to lower level functions and validation errors are handled there.
function generateEntryUrl(localeCode, entityData) {
  if (entityData.enabled) {
    if (
      entityData.contentType === 'ContentfulStandardPage' ||
      entityData.contentType === 'ContentfulWebflowPage'
    ) {
      return generateUrl(localeCode, entityData.pageType, entityData.slug);
    } else if (entityData.contentType === 'ContentfulSubPage') {
      if (
        entityData.parentStructure &&
        entityData.parentStructure.enabled === true
      ) {
        return generateSubPageUrl(
          localeCode,
          entityData.parentStructure.type,
          entityData.parentStructure.slug,
          entityData.slug
        );
      } else {
        console.warn(
          `generateSubPageUrl called with non-enabled parentStructure - ${entityData.parentStructure.slug}, localeCode: ${localeCode}, entitiyData slug: ${entityData.slug}`
        );
        return null;
      }
    } else if (entityData.contentType === 'ContentfulCircularExamplePage') {
      return generateUrl(
        localeCode,
        contentTypeMap[entityData.contentType],
        entityData.slug
      );
    } else {
      console.warn(
        `generateEntryUrl passed unexpected contentType: ${entityData.contentType}`
      );
      return null;
    }
  } else {
    return null;
  }
}

function generateHomePageUrl(localeCode = 'en-GB') {
  return getBaseUrl(localeCode);
}

function generateStructureUrl(localeCode, structureType, structureSlug) {
  return generateUrl(localeCode, structureType, structureSlug);
}

function generateSubPageUrl(
  localeCode,
  structureType,
  structureSlug,
  subPageSlug
) {
  return `${generateStructureUrl(
    localeCode,
    structureType,
    structureSlug
  )}/${subPageSlug}`;
}

function createNavbarLocaleLinks(localeLinks, enabledLocaleCodes) {
  if (Array.isArray(localeLinks)) {
    const navbarLinks = localeLinks.map(link => {
      if (enabledLocaleCodes.includes(link.node_locale) && link.enabled) {
        let pageType = link.pageType;
        let isSubPage = false;

        if (link.internal && link.internal?.type) {
          switch (link.internal.type) {
            case 'ContentfulCircularExamplePage':
              pageType = contentTypeMap[link.internal.type];
              break;
            default:
              pageType = link.pageType;
          }
        }

        if (
          link.parentStructure &&
          link.parentStructure?.type &&
          link.parentStructure.enabled
        ) {
          isSubPage = true;
        }

        const locale = LOCALES.find(({ code }) => code === link.node_locale);
        if (locale && !isSubPage) {
          const url = generateUrl(link.node_locale, pageType, link.slug);
          return {
            code: locale.code,
            url: url,
          };
        }
        if (locale && isSubPage) {
          const url = generateSubPageUrl(
            link.node_locale,
            link.parentStructure.type,
            link.parentStructure.slug,
            link.slug
          );
          return {
            code: locale.code,
            url: url,
          };
        }
        return null;
      } else {
        return {
          code: link.node_locale,
          url: getBaseUrl(link.node_locale),
        };
      }
    });
    return navbarLinks;
  } else {
    console.warn(
      `createNavbarLocaleLinks prop localeLinks is not an array, pageLocales: ${localeLinks}`
    );
    return null;
  }
}

function generatePageLocaleUrls(
  pageLocales,
  currentLocaleCode,
  enabledLocaleCodes
) {
  /*
    See structure of pageLocales from GQL query (templates/standard-page.js)

    pageLocales = [
      {
        id: "xxx",
        enabled: true, 
        node_locale: "en-GB",
        pageType: "video",
        slug: "biomimicry-101"
      },
      {
        ...
      }
    ]
  */

  if (Array.isArray(pageLocales)) {
    const localeLinks = pageLocales
      .map(pageLocale => {
        if (
          enabledLocaleCodes.includes(pageLocale.node_locale) &&
          pageLocale.enabled &&
          pageLocale.node_locale !== currentLocaleCode
        ) {
          const locale = LOCALES.find(
            ({ code }) => code === pageLocale.node_locale
          );
          if (locale) {
            const url = generateUrl(
              pageLocale.node_locale,
              pageLocale.pageType,
              pageLocale.slug
            );
            return {
              title: locale.name,
              code: locale.code,
              url: url,
            };
          }
          return null;
        } else {
          return null;
        }
      })
      .filter(pageLocale => pageLocale !== null);
    return localeLinks;
  } else {
    console.warn(
      `generatePageLocaleUrls prop pageLocales is not an array, pageLocales: ${pageLocales}`
    );
    return null;
  }
}

function generateHomePageLocaleUrls() {
  return LOCALES.map(locale => {
    return {
      title: locale.name,
      code: locale.code,
      url: getBaseUrl(locale.code),
    };
  });
}

function generateCircularExampleLocaleUrls(
  circularExampleLocales,
  currentLocaleCode,
  enabledLocaleCodes
) {
  /*
    See structure of circularExampleLocales from GQL query (templates/circular-examples.js):

    circularExampleLocales = [
      {
        id: "xxx",
        node_locale: "en-GB",
        enabled: true,
        slug: "mud-jeans",
        internal: {
          type: 'ContentfulCircularExamplePage'
        }
      },
      {
        ...
      }
    ]
  */

  return generatePageLocaleUrls(
    circularExampleLocales.map(circularExampleLocale => {
      return {
        ...circularExampleLocale,
        pageType: contentTypeMap[circularExampleLocale.internal.type],
      };
    }),
    currentLocaleCode,
    enabledLocaleCodes
  );
}

function generateSubPageLocaleUrls(
  subPageLocales,
  currentLocaleCode,
  enabledLocaleCodes
) {
  /* 
    Note the GQL filters should ensure both structure and subPage are enabled
    for a given locale, before a link is generated. The enabled property is brought in
    to be checked as a failsafe.

    See structure of subPageLocales from GQL query (templates/structure.js):
  
    subPageLocales = [
      {
        id: "xxx",
        node_locale: "en-GB",
        enabled: true,         
        slug: "overview",
        parentStruture: [
            {
            id: "xxx",
            node_locale: "en-GB",
            enabled: true,
            slug: "food",
            type: "topic"
          }
        ]
      },
      {
        ...
      }
    ]
  */

  if (Array.isArray(subPageLocales)) {
    const localeLinks = subPageLocales
      .map(subPageLocale => {
        if (
          enabledLocaleCodes.includes(subPageLocale.node_locale) &&
          subPageLocale.enabled &&
          subPageLocale.parentStructure.enabled &&
          subPageLocale.node_locale !== currentLocaleCode
        ) {
          const locale = LOCALES.find(
            ({ code }) => code === subPageLocale.node_locale
          );
          if (locale) {
            // generate the localeLink url
            const url = generateSubPageUrl(
              subPageLocale.node_locale,
              subPageLocale.parentStructure.type, // as a pageType, this will be translated using pageTypeTranslations
              subPageLocale.parentStructure.slug, // as a CMS free text field, we need the localised version of the struture slug
              subPageLocale.slug // as a CMS free text field, we need the localised version of the subPage slug
            );
            return {
              title: locale.name,
              code: locale.code,
              url: url,
            };
          }
          return null;
        } else {
          return null;
        }
      })
      .filter(pageLocale => pageLocale !== null);
    return localeLinks;
  } else {
    console.warn(
      `generateSubPageLocaleUrls parameter subPageLocales is not an array, subPageLocales: ${subPageLocales}`
    );
    return null;
  }
}

function generateGCReportUrl(category, companyName, manualSlug) {
  const slug = generateGCReportSlug(category, companyName, manualSlug);
  if (slug) {
    return (
      GLOBAL_COMMITMENT_BASE_PATH +
      '/' +
      GLOBAL_COMMITMENT_LISTING_PAGE_SLUG +
      slug
    );
  }
  return undefined;
}

function generateGCReportSlug(category, companyName, manualSlug) {
  const categoryCode = categoryCodes[category];
  const slug = slugify(manualSlug ? manualSlug : companyName);
  if (categoryCode && slug) {
    return `/${categoryCode}/${slug}`;
  } else {
    console.error(
      `Error in generate-url-functions.js -> generateGCReportSlug. \nSlug not generated (/${categoryCode}/${slug}). \ncategory: ${category}, companyName: ${companyName}, manualSlug: ${manualSlug}`
    );
    return undefined;
  }
}

function generateImageUrl(filename) {
  if (filename.substring(0, 8) === '//images') {
    const imageUrl = 'https:' + filename;
    return imageUrl;
  } else {
    console.error(
      `Error in generate-url-functions.js -> generateImageUrl. \nImage filename did not start with contentful file path //images (${filename}).`
    );
    return filename;
  }
}

function generateSearchHrefLangLinks(canonicalUrl, locale) {
  const exploreLocaleConfig = {
    en: { prefix: '', slug: 'explore' },
    es: { prefix: 'es', slug: 'explora' },
    pt: { prefix: 'pt', slug: 'saiba-mais' },
  };
  if (!canonicalUrl || typeof canonicalUrl !== 'string') {
    return null;
  }
  let url;
  try {
    url = new URL(canonicalUrl);
  } catch (error) {
    console.warn(
      'Invalid canonical URL passed to generateSearchHrefLangLinks:',
      canonicalUrl
    );
    return null;
  }
  const baseUrl = `${url.protocol}//${url.host}`;
  const search = url.search;

  const { prefix, slug } = exploreLocaleConfig[locale];

  const path =
    '/' +
    [prefix, slug]
      .filter(Boolean) // remove empty prefix for "en"
      .join('/');

  return `${baseUrl}${path}${search}`;
}

module.exports = {
  generateCircularExampleLocaleUrls,
  generateEntryUrl,
  generateGCReportSlug,
  generateGCReportUrl,
  generateHomePageLocaleUrls,
  generateHomePageUrl,
  generateImageUrl,
  generatePageLocaleUrls,
  generateStructureUrl,
  createNavbarLocaleLinks,
  generateSubPageLocaleUrls,
  generateSubPageUrl,
  generateUrl,
  getBaseUrl,
  getTranslatedPageTypeSlug,
  generateSearchHrefLangLinks,
};
